/**
 * DANI under staff View-as (M4S-5670, docs/pdlc/cs-view-as-client.md §4
 * "DANI binding (wrong-client guards)").
 *
 * Pure rules, shared by the DANI context, the conversation lifecycle, the send
 * path and the landing page, so each is decided in one place:
 *
 * - which client DANI runs as (the View-as target, never a conversation's tag);
 * - whether plan mode survives a client (`isPlannerAllowedForClient`);
 * - when an opened conversation is locked because it was run as someone else;
 * - the send guard (`assertSendMatchesViewAs`), which refuses with a typed
 *   `ViewAsSendRefusedError` and shows nothing itself: the refusal takes the
 *   path every failed send already takes;
 * - the message for a DANI 403 that is not a credits or usage denial.
 *
 * Nothing here ends View-as. Only Studio's own View-as checks do (§4 step 5).
 */

import { CreditLimitError, UsageLimitError } from '../chat-denial';
import type { AgentMode } from '../types';

/** The client DANI sends as: `masqueradeOrgId` / `masqueradeOrgName`. */
export interface DaniMasqueradeClient {
    id: string;
    name: string;
}

/** The client a tab is viewing as, as the subsidiary provider resolves it. */
export interface DaniViewAsTarget {
    sfAccountId: string;
    orgName: string;
}

/**
 * Whether reports (plan mode) may run as a client: `studio-view-as-reports` on
 * (only once API-1 is deployed, §7), `studio-ai-planner-mode` on, not Freemium.
 * The landing's templates, the effective agent mode and the planner toggle all
 * read this one predicate.
 */
export function isPlannerAllowedForClient(input: {
    viewAsReportsFlag: boolean;
    plannerModeFlag: boolean;
    isFreemium: boolean;
}): boolean {
    return input.viewAsReportsFlag && input.plannerModeFlag && !input.isFreemium;
}

/**
 * Staff who have the View-as control: internal, not a member of a client
 * hierarchy (the provider's own test), not Freemium, with `studio-view-as` on.
 * For them the DANI-only client picker is hidden and a client-tagged
 * conversation opens locked outside View-as, so they always act as a client
 * through a recorded session. Other internal staff keep the picker (D9).
 */
export function hasStaffViewAsControl(input: {
    isInternal: boolean;
    hasSubsidiaryAccess: boolean;
    isUnaffiliated: boolean;
    viewAsFlag: boolean;
}): boolean {
    return (
        input.isInternal && !input.hasSubsidiaryAccess && !input.isUnaffiliated && input.viewAsFlag
    );
}

/**
 * The client DANI runs as. Under global View-as it is always the View-as
 * target, so neither opening a conversation nor `reset()` can change or drop
 * it. Otherwise it is the DANI-only picker's local client.
 */
export function deriveMasqueradeClient(input: {
    viewAsTarget: DaniViewAsTarget | null | undefined;
    localClient: DaniMasqueradeClient | null;
}): DaniMasqueradeClient | null {
    const { viewAsTarget, localClient } = input;
    if (viewAsTarget) return { id: viewAsTarget.sfAccountId, name: viewAsTarget.orgName };
    return localClient;
}

/**
 * The agent mode a send goes with. Any client forces chat mode (the DANI-only
 * picker keeps today's null), except under global View-as when the planner is
 * allowed for a client: then planner turns, follow-ups and "Build a Report"
 * stay on `/plan-mode/chat`.
 */
export function resolveEffectiveAgentMode(input: {
    agentMode: AgentMode;
    masqueradeClient: DaniMasqueradeClient | null;
    isGlobalViewAsActive: boolean;
    plannerAllowedForClient: boolean;
}): AgentMode {
    const { agentMode, masqueradeClient, isGlobalViewAsActive, plannerAllowedForClient } = input;
    if (!masqueradeClient) return agentMode;
    return isGlobalViewAsActive && plannerAllowedForClient ? agentMode : null;
}

/** The input lock for a reopened plan-mode conversation while reports as a client are off. */
export const VIEW_AS_REPORTS_UNAVAILABLE = "Reports as a client aren't available yet";

/**
 * Plan mode asked for under View-as while the planner is not allowed for a
 * client: the composer is locked rather than letting follow-ups go silently
 * to `/chat` (§4 "Plan mode under View-as").
 */
export function isPlannerLockedForClient(input: {
    /** `agentMode === AgentModes.PLANNER`. */
    isPlanner: boolean;
    isGlobalViewAsActive: boolean;
    plannerAllowedForClient: boolean;
}): boolean {
    return input.isGlobalViewAsActive && input.isPlanner && !input.plannerAllowedForClient;
}

/**
 * A conversation that was run as another client, or as the staff member
 * (`sfAccountId: null`), than the tab may act as now.
 */
export interface ViewAsConversationMismatch {
    sfAccountId: string | null;
    orgName: string | null;
}

/** What a loaded conversation says about who it was run as. */
export interface ViewAsConversationTag {
    masqueradeOrgId: string | null;
    masqueradeOrgName: string | null;
    isOwner: boolean;
}

/**
 * Whether an opened conversation must be locked (§4 "Mismatch lock" and
 * "Outside View-as"). Only conversations the user owns are locked; a shared
 * one is read-only already.
 *
 * - Under global View-as: locked unless the conversation's tag is the View-as
 *   client. An untagged conversation was run as the staff member, so it is
 *   locked too.
 * - Outside View-as, for staff with the View-as control: a client-tagged
 *   conversation is locked, so it is continued only through a recorded session.
 * - Anyone else: never locked (today's behaviour, D9).
 */
export function conversationViewAsMismatch(input: {
    conversation: { masqueradeOrgId?: string | null; masqueradeOrgName?: string | null };
    isOwner: boolean;
    isGlobalViewAsActive: boolean;
    targetSfAccountId: string | null;
    hasViewAsControl: boolean;
}): ViewAsConversationMismatch | null {
    if (!input.isOwner) return null;
    const sfAccountId = input.conversation.masqueradeOrgId || null;
    const orgName = sfAccountId ? input.conversation.masqueradeOrgName || sfAccountId : null;
    if (input.isGlobalViewAsActive) {
        return sfAccountId === input.targetSfAccountId ? null : { sfAccountId, orgName };
    }
    if (input.hasViewAsControl && sfAccountId) return { sfAccountId, orgName };
    return null;
}

/** The composer's placeholder while a conversation is locked by a mismatch. */
export function mismatchLockedReason(mismatch: ViewAsConversationMismatch): string {
    return mismatch.sfAccountId
        ? `This conversation was run as ${mismatch.orgName ?? mismatch.sfAccountId}`
        : 'This conversation was run as you';
}

/** What the send path reads from React for the guard, through a ref. */
export interface ViewAsSendGuardState {
    mismatch: ViewAsConversationMismatch | null;
    /** SF ids of the View-as client's group; empty outside View-as. */
    groupSfAccountIds: readonly string[];
    /** The conversation is in plan mode (`agentMode === AgentModes.PLANNER`). */
    plannerSelected?: boolean;
    /** `isPlannerAllowedForClient`. Absent counts as not allowed. */
    plannerAllowedForClient?: boolean;
}

export type ViewAsSendRefusalReason = 'view_as_ended' | 'other_client' | 'reports_unavailable';

const SEND_REFUSAL_MESSAGES: Record<ViewAsSendRefusalReason, string> = {
    view_as_ended: 'View as ended',
    other_client: 'This conversation belongs to another client',
    reports_unavailable: VIEW_AS_REPORTS_UNAVAILABLE,
};

/**
 * A DANI send the View-as guard refused before it left the tab. It is thrown
 * from the mutation, so it reaches the chat error state and the send handlers'
 * one toast like any failed send. It never ends View-as by itself; when the
 * session ended, the end path has already started the reload.
 */
export class ViewAsSendRefusedError extends Error {
    readonly reason: ViewAsSendRefusalReason;

    constructor(reason: ViewAsSendRefusalReason) {
        super(SEND_REFUSAL_MESSAGES[reason]);
        this.name = 'ViewAsSendRefusedError';
        this.reason = reason;
    }
}

/**
 * The send guard (§4 "Send guard"). Throws `ViewAsSendRefusedError` when:
 *
 * - the tab holds a session that is not active, or whose freshness check
 *   failed (`view_as_ended`);
 * - the open conversation is locked by a mismatch, with or without a session
 *   (`other_client`);
 * - the send's resolved `masqueradeOrgId` is not the session's client
 *   (`other_client`). A narrowed org inside the client's group counts as the
 *   client: the group comes from the session's entry org, and the narrowed org
 *   is recorded as `detail.actingSfAccountId`;
 * - plan mode is asked for, by the send or by the conversation, while reports
 *   as a client are off (`reports_unavailable`): a plan-mode send would run on
 *   MFour's data connection where API-1 is not deployed, and a planner
 *   follow-up or "Build a Report" must never go silently to `/chat`.
 */
export function assertSendMatchesViewAs(input: {
    session: { sessionId: string | null; status: string; sfAccountId: string | null };
    fresh: boolean;
    mismatch: ViewAsConversationMismatch | null;
    masqueradeOrgId: string | null | undefined;
    groupSfAccountIds?: readonly string[];
    /** The send is a plan-mode send, or the conversation is in plan mode. */
    plannerRequested?: boolean;
    plannerAllowedForClient?: boolean;
}): void {
    const { session, fresh, mismatch, masqueradeOrgId, groupSfAccountIds = [] } = input;
    if (!session.sessionId) {
        if (mismatch) throw new ViewAsSendRefusedError('other_client');
        return;
    }
    if (!fresh || session.status !== 'active' || !session.sfAccountId) {
        throw new ViewAsSendRefusedError('view_as_ended');
    }
    if (mismatch) throw new ViewAsSendRefusedError('other_client');
    if (input.plannerRequested && !input.plannerAllowedForClient) {
        throw new ViewAsSendRefusedError('reports_unavailable');
    }
    const isSessionClient =
        !!masqueradeOrgId &&
        (masqueradeOrgId === session.sfAccountId || groupSfAccountIds.includes(masqueradeOrgId));
    if (!isSessionClient) throw new ViewAsSendRefusedError('other_client');
}

/** `detail` for a `dani_conversation` row: the narrowed org, when it is not the entry org. */
export function daniConversationDetail(input: {
    masqueradeOrgId: string | null | undefined;
    sessionSfAccountId: string | null;
    isPlanner: boolean;
}): Record<string, unknown> {
    const detail: Record<string, unknown> = { mode: input.isPlanner ? 'planner' : 'chat' };
    if (input.masqueradeOrgId && input.masqueradeOrgId !== input.sessionSfAccountId) {
        detail.actingSfAccountId = input.masqueradeOrgId;
    }
    return detail;
}

/** Why the client can't use DANI. A trial that has ended or not started says so. */
export type ViewAsDaniUnavailableReason = 'no_access' | 'trial_ended' | 'trial_not_started';

const DANI_UNAVAILABLE_MESSAGES: Record<ViewAsDaniUnavailableReason, (client: string) => string> = {
    no_access: (client) => `${client} can't use DANI: no DANI license or no data connection`,
    trial_ended: (client) => `${client}'s DANI trial has ended`,
    trial_not_started: (client) => `${client}'s DANI trial hasn't started yet`,
};

/**
 * A DANI 403 for a client that cannot use DANI: no DANI access or no data
 * connection (the Kochava case), or a trial that has ended or not started, each
 * named so CS know what to fix. Credits and usage denials keep their own errors.
 */
export class ViewAsDaniUnavailableError extends Error {
    readonly reason: ViewAsDaniUnavailableReason;

    constructor(clientName: string, reason: ViewAsDaniUnavailableReason = 'no_access') {
        super(DANI_UNAVAILABLE_MESSAGES[reason](clientName));
        this.name = 'ViewAsDaniUnavailableError';
        this.reason = reason;
    }
}

/**
 * The bodies of the API's 403s from `/chat` and `/plan-mode/chat` that are not
 * metering denials, as `sendChatMessage` surfaces them: Nest's own `Forbidden`
 * for a string ForbiddenException (no Studio AI access, masquerade refused),
 * and the access guard's trial refusals. `sendChatMessage` drops the HTTP
 * status, so these are recognised by text; an error that carries
 * `status: 403` is recognised by that.
 */
const FORBIDDEN_CHAT_MESSAGES = new Set([
    'forbidden',
    'you do not have access to studio ai features',
    'trial not started',
    'trial ended',
]);

/** The agent's (and the API's Sigma lookup's) "no data connection" refusal. */
const NO_CONNECTION_PROFILE = /no (snowflake|sigma) connection profile/i;

/** Whether a DANI error message is the agent's "no connection profile" refusal. */
export function isNoConnectionProfileMessage(message: string | null | undefined): boolean {
    return !!message && NO_CONNECTION_PROFILE.test(message);
}

/**
 * Maps a failed DANI send under View-as to `ViewAsDaniUnavailableError` (with
 * the trial named when the trial is the reason), or returns null to keep the
 * error as it is. Maps a 403 that
 * `errorFromChatDenialBody` did not turn into a credits or usage error, and the
 * "no connection profile" refusal. A `CreditLimitError` or `UsageLimitError`
 * keeps its own message (the client's own credit message, under D5), and so
 * does a refusal by the send guard.
 */
export function viewAsChatDenial(
    error: unknown,
    clientName: string | null | undefined,
): ViewAsDaniUnavailableError | null {
    if (!clientName || !(error instanceof Error)) return null;
    if (
        error instanceof CreditLimitError ||
        error instanceof UsageLimitError ||
        error instanceof ViewAsSendRefusedError ||
        error instanceof ViewAsDaniUnavailableError
    ) {
        return null;
    }
    const message = error.message.trim();
    if (/credit limit|usage limit/i.test(message)) return null;
    const status = (error as { status?: unknown }).status;
    const lower = message.toLowerCase();
    const isForbidden = status === 403 || FORBIDDEN_CHAT_MESSAGES.has(lower);
    if (!isForbidden && !isNoConnectionProfileMessage(message)) return null;
    // The access guard's trial refusals (`studio-ai-access.guard.ts`).
    if (lower === 'trial ended') return new ViewAsDaniUnavailableError(clientName, 'trial_ended');
    if (lower === 'trial not started') {
        return new ViewAsDaniUnavailableError(clientName, 'trial_not_started');
    }
    return new ViewAsDaniUnavailableError(clientName);
}
